import { AddIcon, CloseIcon } from "@chakra-ui/icons"
import {
	Box,
	Button,
	Flex,
	FormControl,
	IconButton,
	Input,
	NumberDecrementStepper,
	NumberIncrementStepper,
	NumberInput,
	NumberInputField,
	NumberInputStepper,
	Stack,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
} from "@chakra-ui/react"
import { ErrorMessage, Formik } from "formik"
import moment from "moment"
import React, { ComponentProps, FC, useState } from "react"
import { ReactSelect, SelectOption } from "src/components/shared/ReactSelect"
import { ErrorMessageField } from "src/components/ui"
import { DrawerForm } from "src/components/ui/DrawerForm"
import { InputLabel } from "src/components/ui/InputLabel"
import { IHallBooking, IHallBookingReceipt, IItem } from "src/domain/entities"
import { FormikOnSubmit } from "src/utils/types"
import { IHallBookingBillAddFormFields } from "."
import { getFullName, toINR } from "src/utils/helpers"
import {
	calculateUtilityAmount,
	DEFAULT_ELECTRICITY_PRICE_PER_UNIT,
	DEFAULT_GAS_PRICE_PER_UNIT,
} from "../hallBookingBillUtilityCharge"

interface Props extends Omit<ComponentProps<typeof DrawerForm>, "children"> {
	handleSubmit: FormikOnSubmit<IHallBookingBillAddFormFields>
	hallBookingReceiptList: IHallBookingReceipt[]
	itemList: IItem[]
	hallBooking?: IHallBooking
}

export const HallBookingBillAddDrawerFormView: FC<Props> = ({
	handleSubmit,
	hallBookingReceiptList,
	itemList,
	hallBooking,
	...rest
}) => {
	const [items, setItems] = useState<IHallBookingBillAddFormFields["items"]>([])

	const addItem = () => {
		setItems([...items, { itemId: "", quantity: 1, days: 1 }])
	}

	const removeItem = (index: number) => {
		setItems(items.filter((_, i) => i !== index))
	}

	const updateItem = (
		index: number,
		field: keyof IHallBookingBillAddFormFields["items"][0],
		value: any,
	) => {
		const newItems = [...items]
		newItems[index] = { ...newItems[index], [field]: value }
		setItems(newItems)
	}

	// Update form values when items change
	React.useEffect(() => {
		// This will be handled in the Formik component
	}, [items])

	// Function to get current items for form submission
	const getCurrentItems = () => items

	return (
		<Formik<IHallBookingBillAddFormFields>
			initialValues={{
				billNo: "",
				date: moment().format("YYYY-MM-DD"),
				incomeId: "",
				electricity: {
					previousReading: 0,
					currentReading: 0,
					pricePerUnit: DEFAULT_ELECTRICITY_PRICE_PER_UNIT,
					amount: 0,
				},
				gas: {
					previousReading: 0,
					currentReading: 0,
					pricePerUnit: DEFAULT_GAS_PRICE_PER_UNIT,
					amount: 0,
				},
				items: [],
			}}
			onSubmit={(values, formikHelpers) => {
				// Include the current items state in the submission
				const formDataWithItems = {
					...values,
					items: getCurrentItems(),
				}
				handleSubmit(formDataWithItems, formikHelpers)
			}}
			enableReinitialize
		>
			{({ values, isSubmitting, handleChange, setFieldValue }) => {
				// Update form values when items change
				// Note: items are managed separately and will be included in form submission

				return (
					<DrawerForm
						size="lg"
						headerLabel="Add Hall Booking Bill"
						submitLabel="Save"
						isSubmitting={isSubmitting}
						{...rest}
					>
						<Stack marginX={"auto"} spacing={4}>
							<Flex gap={4}>
								{/* Bill Number */}
								<FormControl>
									<InputLabel label="Bill Number" />
									<Input
										name="billNo"
										placeholder="VVLP/001/2026-27"
										value={values.billNo}
										onChange={handleChange}
										required
									/>
									<ErrorMessage
										component={ErrorMessageField}
										name="billNo"
									/>
								</FormControl>

								{/* Date */}
								<FormControl>
									<InputLabel label="Date" />
									<Input
										name="date"
										type="date"
										value={values.date}
										onChange={handleChange}
										required
									/>
								</FormControl>
							</Flex>

							{/* Income */}
							<FormControl>
								<InputLabel label="Income" />
								<ReactSelect
									name="incomeId"
									onChange={(newValue) => {
										setFieldValue(
											"incomeId",
											(newValue as SelectOption).value,
										)
									}}
									options={hallBookingReceiptList.map((income) => {
										const amount = income.fields?.find(
											(field) => field.field.name === "Amount",
										)?.value
										const contactName = getFullName(
											income.fields?.find(
												(f) => f.field.name === "Contact",
											)?.value,
										)
										return {
											label: `Income #${
												income.hallBookingReceiptNo
											} - ${contactName} - ${toINR(amount)}`,
											value: income.id,
										}
									})}
									isSearchable
									placeholder="Select Income"
								/>
							</FormControl>

							{/* Electricity Section */}
							<Box borderWidth={1} borderRadius="md" p={4}>
								<Text fontWeight="bold" mb={3}>
									Electricity
								</Text>
								<Flex gap={2} flexWrap="wrap">
									<FormControl flex="1" minW="140px">
										<InputLabel label="Previous Reading" />
										<NumberInput
											value={values.electricity.previousReading}
											onChange={(_, value) => {
												setFieldValue(
													"electricity.previousReading",
													value,
												)
												setFieldValue(
													"electricity.amount",
													calculateUtilityAmount(
														value,
														values.electricity.currentReading,
														values.electricity.pricePerUnit,
													),
												)
											}}
										>
											<NumberInputField />
											<NumberInputStepper>
												<NumberIncrementStepper />
												<NumberDecrementStepper />
											</NumberInputStepper>
										</NumberInput>
									</FormControl>
									<FormControl flex="1" minW="140px">
										<InputLabel label="Current Reading" />
										<NumberInput
											value={values.electricity.currentReading}
											onChange={(_, value) => {
												setFieldValue(
													"electricity.currentReading",
													value,
												)
												setFieldValue(
													"electricity.amount",
													calculateUtilityAmount(
														values.electricity.previousReading,
														value,
														values.electricity.pricePerUnit,
													),
												)
											}}
										>
											<NumberInputField />
											<NumberInputStepper>
												<NumberIncrementStepper />
												<NumberDecrementStepper />
											</NumberInputStepper>
										</NumberInput>
									</FormControl>
									<FormControl flex="1" minW="140px">
										<InputLabel label="Price per Unit" />
										<NumberInput
											value={values.electricity.pricePerUnit}
											onChange={(_, value) => {
												setFieldValue(
													"electricity.pricePerUnit",
													value,
												)
												setFieldValue(
													"electricity.amount",
													calculateUtilityAmount(
														values.electricity.previousReading,
														values.electricity.currentReading,
														value,
													),
												)
											}}
										>
											<NumberInputField />
											<NumberInputStepper>
												<NumberIncrementStepper />
												<NumberDecrementStepper />
											</NumberInputStepper>
										</NumberInput>
									</FormControl>
									<FormControl flex="1" minW="140px">
										<InputLabel label="Amount" />
										<NumberInput
											value={values.electricity.amount}
											onChange={(_, value) =>
												setFieldValue("electricity.amount", value)
											}
											isReadOnly
										>
											<NumberInputField />
										</NumberInput>
									</FormControl>
								</Flex>
							</Box>

							{/* Gas Section */}
							<Box borderWidth={1} borderRadius="md" p={4}>
								<Text fontWeight="bold" mb={3}>
									Gas
								</Text>
								<Flex gap={2} flexWrap="wrap">
									<FormControl flex="1" minW="140px">
										<InputLabel label="Previous Reading" />
										<NumberInput
											value={values.gas.previousReading}
											onChange={(_, value) => {
												setFieldValue(
													"gas.previousReading",
													value,
												)
												setFieldValue(
													"gas.amount",
													calculateUtilityAmount(
														value,
														values.gas.currentReading,
														values.gas.pricePerUnit,
													),
												)
											}}
										>
											<NumberInputField />
											<NumberInputStepper>
												<NumberIncrementStepper />
												<NumberDecrementStepper />
											</NumberInputStepper>
										</NumberInput>
									</FormControl>
									<FormControl flex="1" minW="140px">
										<InputLabel label="Current Reading" />
										<NumberInput
											value={values.gas.currentReading}
											onChange={(_, value) => {
												setFieldValue("gas.currentReading", value)
												setFieldValue(
													"gas.amount",
													calculateUtilityAmount(
														values.gas.previousReading,
														value,
														values.gas.pricePerUnit,
													),
												)
											}}
										>
											<NumberInputField />
											<NumberInputStepper>
												<NumberIncrementStepper />
												<NumberDecrementStepper />
											</NumberInputStepper>
										</NumberInput>
									</FormControl>
									<FormControl flex="1" minW="140px">
										<InputLabel label="Price per Unit" />
										<NumberInput
											value={values.gas.pricePerUnit}
											onChange={(_, value) => {
												setFieldValue("gas.pricePerUnit", value)
												setFieldValue(
													"gas.amount",
													calculateUtilityAmount(
														values.gas.previousReading,
														values.gas.currentReading,
														value,
													),
												)
											}}
										>
											<NumberInputField />
											<NumberInputStepper>
												<NumberIncrementStepper />
												<NumberDecrementStepper />
											</NumberInputStepper>
										</NumberInput>
									</FormControl>
									<FormControl flex="1" minW="140px">
										<InputLabel label="Amount" />
										<NumberInput
											value={values.gas.amount}
											onChange={(_, value) =>
												setFieldValue("gas.amount", value)
											}
											isReadOnly
										>
											<NumberInputField />
										</NumberInput>
									</FormControl>
								</Flex>
							</Box>

							{/* Items Section */}
							<Box borderWidth={1} borderRadius="md" p={4}>
								<Flex
									justifyContent="space-between"
									alignItems="center"
									mb={3}
								>
									<Text fontWeight="bold">Items</Text>
									<Button
										size="sm"
										onClick={addItem}
										leftIcon={<AddIcon />}
									>
										Add Item
									</Button>
								</Flex>

								{items.length > 0 && (
									<Table size="sm">
										<Thead>
											<Tr>
												<Th>Item</Th>
												<Th>Quantity</Th>
												<Th>Days</Th>
												<Th>Action</Th>
											</Tr>
										</Thead>
										<Tbody>
											{items.map((item, index) => (
												<Tr key={index}>
													<Td width="50%">
														<ReactSelect
															value={
																itemList.find(
																	(i) =>
																		i.id ===
																		item.itemId,
																)
																	? {
																			label:
																				itemList.find(
																					(i) =>
																						i.id ===
																						item.itemId,
																				)?.name ||
																				"",
																			value: item.itemId,
																	  }
																	: undefined
															}
															onChange={(newValue) => {
																updateItem(
																	index,
																	"itemId",
																	(
																		newValue as SelectOption
																	)?.value || "",
																)
															}}
															options={itemList.map(
																(item) => ({
																	label: item.name,
																	value: item.id,
																}),
															)}
															placeholder="Select Item"
														/>
													</Td>
													<Td>
														<NumberInput
															value={item.quantity}
															onChange={(_, value) =>
																updateItem(
																	index,
																	"quantity",
																	value,
																)
															}
															min={1}
														>
															<NumberInputField />
															<NumberInputStepper>
																<NumberIncrementStepper />
																<NumberDecrementStepper />
															</NumberInputStepper>
														</NumberInput>
													</Td>
													<Td>
														<NumberInput
															value={item.days}
															onChange={(_, value) =>
																updateItem(
																	index,
																	"days",
																	value,
																)
															}
															min={1}
														>
															<NumberInputField />
															<NumberInputStepper>
																<NumberIncrementStepper />
																<NumberDecrementStepper />
															</NumberInputStepper>
														</NumberInput>
													</Td>
													<Td>
														<IconButton
															aria-label="Remove item"
															icon={<CloseIcon />}
															size="sm"
															onClick={() =>
																removeItem(index)
															}
															colorScheme="red"
															variant="ghost"
														/>
													</Td>
												</Tr>
											))}
										</Tbody>
									</Table>
								)}
							</Box>

							{/* Hall Booking Info */}
							{hallBooking && (
								<Box borderWidth={1} borderRadius="md" p={4} bg="gray.50">
									<Text fontWeight="bold" mb={2}>
										Hall Booking Details
									</Text>
									<Text>Location: {hallBooking.location?.name}</Text>
									<Text>
										Contact: {hallBooking.contact?.firstName}{" "}
										{hallBooking.contact?.lastName}
									</Text>
									<Text>Reason: {hallBooking.reason}</Text>
									<Text>
										Start Date:{" "}
										{moment(hallBooking.startDate).format(
											"DD/MM/YYYY",
										)}
									</Text>
									<Text>
										End Date:{" "}
										{moment(hallBooking.endDate).format("DD/MM/YYYY")}
									</Text>
									<Text>Deposit: ₹{hallBooking.deposit}</Text>
								</Box>
							)}
						</Stack>
					</DrawerForm>
				)
			}}
		</Formik>
	)
}
